import {
  useCallback,
  useRef,
  useState,
  DragEvent,
  useMemo,
  useEffect,
} from 'react';
import {
  Text,
  VStack,
  Image,
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  DrawerCloseButton,
  useDisclosure,
  Button,
} from '@chakra-ui/react';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Connection,
  Controls,
  ReactFlowInstance,
  addEdge,
  useEdgesState,
  useNodesState,
  Node,
  XYPosition,
  MarkerType,
  Edge,
} from 'reactflow';
import { useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import {
  ReceiverNode,
  ConditionalNode,
  StartNode,
  ReceiverNodeEditorModal,
  EndNode,
  NormalEdge,
  ConditionalEdge,
  RejectedEdge,
  RejectionReceiverNodeModal,
  WorkflowDurationModalOpen,
} from '..';
import {
  useCreateWorkflowMutation,
  useGetFormTemplateByIdQuery,
  useGetWorkflowByIdQuery,
  useUpdateWorkflowMutation,
} from '../../../../services';
import ReceiverNodeIcon from '../../../../assets/projects/receiver_node.svg';
import ConditionalNodeIcon from '../../../../assets/projects/conditional_node.svg';
import { ConditionalNodeEditorModal } from './ConditionalNodeEditorModal';
import {
  ConditionalNodeData,
  ReceivedNodeData,
  WorkflowDurationForm,
} from '../../models';
import { InputToggler } from '../../../../components';
import { DurationUnit, Form, WorkflowBuilderMode } from '../../../../models';
import { pascalCaseToString, stringToPascalCase } from '../../../../utils';
import { END_NODE_WIDTH, START_NODE_WIDTH } from '../../../../constants';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../../utils/mutation-feedback';

export const WorkflowBuilder = ({
  isOpen,
  onClose,
  formId,
  phaseId,
  workflowId,
  mode = WorkflowBuilderMode.View,
}: {
  isOpen: boolean;
  onClose: () => void;
  phaseId: number;
  formId: number;
  workflowId?: number;
  mode?: WorkflowBuilderMode;
}) => {
  const [workflowName, setWorkflowName] = useState<string>('');
  const [workflowDuration, setWorkflowDuration] =
    useState<WorkflowDurationForm>();
  const [isDurationUpdated, setIsDurationUpdated] = useState(false);
  const [isWorkflowNameInvalid, setIsWorkflowNameInvalid] = useState(false);
  const [position, setPosition] = useState<XYPosition>();
  const [sourceNode, setSourceNode] = useState<Node<ConditionalNodeData>>();
  const [connection, setConnection] = useState<Connection>();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance<null, null>>();

  const { projectId } = useParams();

  const nodeTypes = useMemo(
    () => ({
      Start: StartNode,
      End: EndNode,
      Receiver: ReceiverNode,
      Conditional: ConditionalNode,
    }),
    []
  );

  const edgeTypes = useMemo(
    () => ({
      Normal: NormalEdge,
      Rejected: RejectedEdge,
      Conditional: ConditionalEdge,
    }),
    []
  );

  const {
    isOpen: isReceiverNodeEditorModalOpen,
    onOpen: onReceiverNodeEditorModalOpen,
    onClose: onReceiverNodeEditorModalClose,
  } = useDisclosure();

  const {
    isOpen: isConditionalNodeEditorModalOpen,
    onOpen: onConditionalNodeEditorModalOpen,
    onClose: onConditionalNodeEditorModalClose,
  } = useDisclosure();

  const {
    isOpen: HasRejectionReceiverNodeModalOpen,
    onOpen: onHasRejectionReceiverNodeModalOpen,
    onClose: onHasRejectionReceiverNodeModalClose,
  } = useDisclosure();

  const {
    isOpen: isWorkflowDurationModalOpen,
    onOpen: onWorkflowDurationModalOpen,
    onClose: onWorkflowDurationModalClose,
  } = useDisclosure();

  const { data: workflowData } = useGetWorkflowByIdQuery(workflowId, {
    skip: !workflowId,
  });

  const { data: formTemplateData } = useGetFormTemplateByIdQuery(formId, {
    skip: Boolean(workflowId),
  });

  const [createWorkflowMutation, { isLoading: isCreateWorkflowLoading }] =
    useCreateWorkflowMutation();
  const [updateWorkflow, { isLoading: isUpdateWorkflowLoading }] =
    useUpdateWorkflowMutation();

  useEffect(() => {
    if (workflowData?.data) {
      const { name, nodes, edges, form } = workflowData.data;
      setWorkflowName(name);
      setWorkflowDuration({
        durationUnit: workflowData?.data?.durationUnit,
        durationValue: workflowData?.data?.durationValue,
      });
      const customNodes = nodes.map(
        ({
          id,
          xAxis,
          yAxis,
          type,
          receivedRoles,
          receivedUsers,
          hasRejection,
          durationUnit,
          durationValue,
        }) => {
          const edge = edges.find(edge => edge.source === id);

          let node = {
            id,
            type,
            position: {
              x: xAxis,
              y: yAxis,
            },
            data: {},
          };

          if (type === 'Receiver') {
            node = {
              ...node,
              data: {
                receivedRoles,
                receivedUsers: receivedUsers?.map(({ id, name }) => ({
                  id,
                  firstName: name,
                })),
                hasRejection,
                durationUnit: {
                  value: durationUnit as string,
                  label: `${durationUnit as string}s`,
                },
                durationValue,
              },
            };
          } else if (type === 'Conditional' && edge) {
            const { conditionField } = edge;

            const field = workflowData.data.form.fields.find(
              field => field.title === conditionField
            );

            node = {
              ...node,
              data: {
                conditionField: {
                  conditionFieldId: field?.id,
                  title: conditionField,
                },
                form,
              },
            };
          }

          return node;
        }
      ) as Node[];

      setNodes(customNodes);

      const customEdges = edges.map(
        ({
          target,
          source,
          type,
          conditionField,
          conditionOperation,
          conditionValue1,
          conditionValue2,
        }) => {
          let edge = {
            id: uuid(),
            target,
            source,
            type,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 14,
              height: 14,
            },
            data: {},
          };

          if (type === 'Conditional') {
            const field = form.fields.find(
              field => field.title === conditionField
            );

            edge = {
              ...edge,
              data: {
                conditionField: {
                  conditionFieldId: field?.id,
                  title: conditionField,
                },
                conditionOperation,
                conditionValue1,
                conditionValue2,
                form,
              },
              label: `${pascalCaseToString(
                conditionOperation
              )} ${conditionValue1} ${conditionValue2}`,
            };
          }
          return edge;
        }
      ) as Edge[];

      setEdges(customEdges);
    } else {
    }
  }, [workflowData?.data]);

  useEffect(() => {
    if (reactFlowWrapper.current && mode === WorkflowBuilderMode.Create) {
      const startNodePosition = {
        x: reactFlowWrapper.current!.clientWidth / 2 - START_NODE_WIDTH / 2,
        y: reactFlowWrapper.current!.clientHeight / 5,
      };

      const endNodePosition = {
        x: reactFlowWrapper.current!.clientWidth / 2 - END_NODE_WIDTH / 2,
        y: (4 * reactFlowWrapper.current!.clientHeight) / 5,
      };

      setNodes([
        {
          id: uuid(),
          type: 'Start',
          position: startNodePosition,
          data: {},
        },
        {
          id: uuid(),
          type: 'End',
          position: endNodePosition,
          data: {},
        },
      ]);
    }
  }, [reactFlowWrapper.current]);

  useEffect(() => {
    if (isDurationUpdated) handleSaveWorkFlow();
  }, [isDurationUpdated]);

  const onConnect = useCallback(
    (connection: Connection) => {
      const sourceNode = nodes.find(node => node.id === connection.source);

      if (
        sourceNode &&
        ['Conditional', 'Receiver'].includes(
          sourceNode.type as keyof typeof nodeTypes
        )
      ) {
        setSourceNode(sourceNode);
        setConnection(connection);

        if (sourceNode.type === 'Conditional')
          return onConditionalNodeEditorModalOpen();

        if (sourceNode.type === 'Receiver' && sourceNode.data.hasRejection)
          return onHasRejectionReceiverNodeModalOpen();
      }

      return setEdges(eds =>
        addEdge(
          {
            ...connection,
            id: uuid(),
            type: 'Normal',
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#B3B3B3',
              width: 14,
              height: 14,
            },
          },
          eds
        )
      );
    },
    [nodes, onConditionalNodeEditorModalOpen, setEdges]
  );

  const onDragStart = (
    event: DragEvent<HTMLDivElement>,
    nodeType: keyof typeof nodeTypes
  ) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      const type = event.dataTransfer.getData(
        'application/reactflow'
      ) as keyof typeof nodeTypes;

      // check if the dropped element is valid
      if (typeof type === 'undefined' || !type || !reactFlowInstance) {
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      setPosition(position);

      const node: Node = {
        id: uuid(),
        type,
        position,
        data: {},
      };

      if (['Start', 'End'].includes(type)) setNodes(nds => nds.concat(node));
      if (type === 'Receiver') onReceiverNodeEditorModalOpen();
      if (type === 'Conditional') onConditionalNodeEditorModalOpen();
    },
    [
      onConditionalNodeEditorModalOpen,
      onReceiverNodeEditorModalOpen,
      reactFlowInstance,
      setNodes,
    ]
  );

  const handleSaveWorkFlow = async () => {
    setIsDurationUpdated(false);
    if (!workflowName) return setIsWorkflowNameInvalid(true);
    const data = {
      name: workflowName,
      ...workflowDuration,
      edges: edges.map(({ type, source, target, data }) => {
        const edge = {
          type: type as keyof typeof edgeTypes,
          source: source,
          target: target,
        };

        if (type === 'Conditional') {
          edge.conditionField = data.conditionField.title;
          edge.conditionValue1 = data.conditionValue1;
          edge.conditionValue2 = data.conditionValue2;
          edge.conditionOperation = stringToPascalCase(data.conditionOperation);
        }

        return edge;
      }),
      nodes: nodes.map(({ id, type, position, data }) => {
        const node = {
          id,
          type: type as keyof typeof nodeTypes,
          xAxis: position.x,
          yAxis: position.y,
          receivedRoles: [] as any[],
          receivedUsers: [] as any[],
          hasRejection: false,
          durationUnit: undefined,
          durationValue: undefined,
        };

        if (node.type === 'Receiver') {
          const {
            receivedRoles,
            receivedUsers,
            hasRejection,
            durationUnit,
            durationValue,
          } = data as ReceivedNodeData;
          node.receivedRoles = receivedRoles.map(({ id, name }) => ({
            id: String(id),
            name,
          }));
          node.durationUnit = durationUnit?.value as DurationUnit;
          node.durationValue = durationValue;
          node.receivedUsers = receivedUsers.map(({ id, firstName }) => ({
            id: String(id),
            // TODO: change after backend change
            name: firstName,
          }));
          node.hasRejection = hasRejection;
        }

        return node;
      }),
    };

    let response: unknown;

    if (mode === WorkflowBuilderMode.Create) {
      // for cloned workflow we use the form itself instead of form template
      const formIdType = workflowId ? 'formId' : 'formTemplateId';
      response = await createWorkflowMutation({
        ...data,
        [formIdType]: Number(formId),
        phaseId,
      });
    } else {
      response = await updateWorkflow({
        ...workflowData!.data,
        ...data,
      });
    }

    // Neither trigger is unwrapped, so a rejected save resolved normally and
    // the drawer closed as if the workflow had been published — discarding the
    // entire node graph the user had just built. Only a body-level success
    // closes it now; on any failure the drawer stays open with every node,
    // edge and the workflow name exactly where they were, so the user can
    // retry without rebuilding the diagram.
    if (!isMutationSuccess(response)) {
      notifyMutationFailure(response, {
        title:
          mode === WorkflowBuilderMode.Create
            ? 'Could not publish this workflow'
            : 'Could not save this workflow',
        description:
          'Your workflow was not saved — it is still here. Please try again.',
      });
      return;
    }

    setIsDurationUpdated(false);
    onClose();
  };

  return (
    <Drawer placement="bottom" onClose={onClose} isOpen={isOpen}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton color="white" top="-14" size="lg" />

        <DrawerHeader borderBottomWidth="1px">
          <HStack justify="space-between">
            <Box>
              <InputToggler
                placeholder="Name Workflow"
                inputValue={workflowName}
                onInputChange={value => {
                  setWorkflowName(value);
                  setIsWorkflowNameInvalid(!value);
                }}
                alwaysHideInput={mode === WorkflowBuilderMode.View}
                isInvalid={isWorkflowNameInvalid}
              />
            </Box>
            {mode !== WorkflowBuilderMode.View && (
              <Button
                bg="custom_orange.2"
                _hover={{ bg: 'custom_orange.2' }}
                color="white"
                type="submit"
                onClick={onWorkflowDurationModalOpen}
                isLoading={isCreateWorkflowLoading || isUpdateWorkflowLoading}
                isDisabled={isWorkflowNameInvalid}
              >
                {mode === WorkflowBuilderMode.Create ? 'Publish' : 'Save'}
              </Button>
            )}
          </HStack>
        </DrawerHeader>
        <DrawerBody>
          {isConditionalNodeEditorModalOpen && (
            <ConditionalNodeEditorModal
              isOpen={isConditionalNodeEditorModalOpen}
              onClose={newData => {
                onConditionalNodeEditorModalClose();
                setSourceNode(undefined);
                setConnection(undefined);

                if (!newData) return;

                const { conditionOperation, conditionValue1, conditionValue2 } =
                  newData;

                if (
                  conditionOperation &&
                  (conditionValue1 ||
                    ['empty', 'not empty'].includes(conditionOperation))
                ) {
                  // Set Conditional Field Data
                  if (connection)
                    setEdges(eds =>
                      addEdge(
                        {
                          ...connection,
                          type: 'Conditional',
                          id: uuid(),
                          markerEnd: {
                            type: MarkerType.ArrowClosed,
                            width: 14,
                            height: 14,
                          },
                          data: newData,
                          label: `${conditionOperation} ${
                            conditionValue1 || ''
                          } ${conditionValue2 || ''}`,
                        },
                        eds
                      )
                    );
                } else {
                  // Select Conditional Field
                  if (!position) return;
                  const node: Node<ConditionalNodeData> = {
                    id: uuid(),
                    type: 'Conditional',
                    position,
                    data: { ...newData },
                  };

                  setNodes(nds => nds.concat(node));
                }
              }}
              form={(workflowData?.data.form || formTemplateData?.data) as Form}
              conditionField={sourceNode?.data.conditionField}
            />
          )}

          {isReceiverNodeEditorModalOpen && (
            <ReceiverNodeEditorModal
              isOpen={isReceiverNodeEditorModalOpen}
              onClose={newData => {
                onReceiverNodeEditorModalClose();
                if (!position || !newData) return;

                const node: Node = {
                  id: uuid(),
                  type: 'Receiver',
                  position,
                  data: { ...newData },
                };

                setNodes(nds => nds.concat(node));
              }}
              receivedNodeData={{
                receivedRoles: [],
                receivedUsers: [],
                hasRejection: false,
                durationUnit: undefined,
                durationValue: undefined,
              }}
              phaseId={String(phaseId)}
              projectId={String(projectId)}
            />
          )}

          {HasRejectionReceiverNodeModalOpen && (
            <RejectionReceiverNodeModal
              isOpen={HasRejectionReceiverNodeModalOpen}
              onClose={isRejected => {
                onHasRejectionReceiverNodeModalClose();
                setSourceNode(undefined);
                setConnection(undefined);

                if (!connection) return;

                let edge = {
                  ...connection,
                  id: uuid(),
                  data: {},
                };

                if (isRejected) {
                  edge = {
                    ...edge,
                    type: 'Rejected',
                    markerEnd: {
                      type: MarkerType.ArrowClosed,
                      width: 14,
                      height: 14,
                      color: '#FF0000',
                    },
                    label: `Rejected`,
                  };
                } else {
                  edge = {
                    ...edge,
                    type: 'Normal',
                  };
                }

                setEdges(eds => addEdge(edge, eds));
              }}
            />
          )}

          {isWorkflowDurationModalOpen && (
            <WorkflowDurationModalOpen
              isOpen={isWorkflowDurationModalOpen}
              workflowDuration={workflowDuration}
              onClose={workflowDuration => {
                onWorkflowDurationModalClose();
                if (workflowDuration) {
                  setWorkflowDuration(workflowDuration);
                  setIsDurationUpdated(true);
                }
              }}
            />
          )}

          <VStack h="80vh" align="stretch">
            <Box h="full" ref={reactFlowWrapper}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onInit={setReactFlowInstance}
                onDrop={onDrop}
                onDragOver={onDragOver}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
              >
                <Controls />
                <Background variant={BackgroundVariant.Dots} />
              </ReactFlow>
            </Box>

            {mode !== WorkflowBuilderMode.View && (
              <HStack
                w="22%"
                m="auto"
                justify="space-around"
                align="center"
                border="1px"
                borderColor="custom_gray.6"
                borderRadius="md"
                p="4"
              >
                <Box
                  border="1px"
                  borderRadius="md"
                  borderColor="blackAlpha.100"
                  w="32"
                  h="16"
                  onDragStart={event => onDragStart(event, 'Receiver')}
                  draggable
                  p="2"
                >
                  <VStack
                    border="1px"
                    borderColor="custom_purple.1"
                    borderRadius="md"
                  >
                    <HStack
                      bg="custom_purple.1"
                      w="full"
                      flex="1"
                      justify="center"
                    >
                      <Image src={ReceiverNodeIcon} />
                    </HStack>

                    <HStack flex="1">
                      <Text fontSize="xs" fontWeight="medium">
                        Receiver Node
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
                <Box
                  border="1px"
                  borderRadius="md"
                  borderColor="blackAlpha.100"
                  w="32"
                  h="16"
                  onDragStart={event => onDragStart(event, 'Conditional')}
                  draggable
                  p="2"
                >
                  <VStack
                    border="1px"
                    borderColor="custom_yellow.2"
                    borderRadius="md"
                  >
                    <HStack
                      bg="custom_yellow.2"
                      w="full"
                      flex="1"
                      justify="center"
                    >
                      <Image src={ConditionalNodeIcon} />
                    </HStack>

                    <HStack flex="1">
                      <Text fontSize="xs" fontWeight="medium">
                        Conditional Node
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              </HStack>
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
